import {createSlice, createAsyncThunk, current} from "@reduxjs/toolkit";
import { api, apiErrorsHandler } from "../../api/httpclient";
import { addNotification } from "./notificationsslice";
import { notificationDurationShort, notificationDurationMiddle, notificationDurationLong } from "../../lib/utils";

const defaultTypes = [{Id: 0, Title: "None"}, {Id: 1, Title: "User"}, {Id: 2, Title: "Client"},{Id: 3, Title: "Client User"}];
const defaultTypeId = 0;

const defaultFormInitialValues = {
    Id: 0, 
    Name: "",
    Email: "",
    Description: "", 
    IsActive: true,
    IsRootSubscription: false,
    IsFederationRoot: false,
    TypeId: defaultTypeId,
    ClientId: "",
    GroupsIds: [],
    Groups: [],
    Username: "",
    Password: "",
    PasswordConfirmed: ""
};

const initialState = {
    pageLoaded: false, pageDisposed: true,
    processing: false, processingText: "",
    mode: 0, 
    formKey: (new Date()).toString(), formInitialValues: {...defaultFormInitialValues}, formValues: {...defaultFormInitialValues}, formClientErrors: {}, formServerErrors: [], 
    lookups: {
        groups: [],
        types: defaultTypes
    },
    entities: [],
    
    toolsVisible: true,
    panes: [                    
        {
            // size: "30%",
            size:"20%",
            // min: "17%",
            // max: "22%",
            collapsible: false
        },           
        {
            //min: "700px",
            //size:"60%",
            min: "60%",
            max: "80%",
            collapsible: false,
        },
        {
            //min: "700px",
            size:"10%",
            min: "0%",
            max: "20%",
            collapsible: false,
        }
    ],
};

const usersPageSlice = createSlice({
    name: "usersPage",
    initialState: initialState,
    reducers:{
        setInitialState: (state, action) =>  ({...initialState}),

        setProcessing: (state, action) => {
            //payload: {processing=true, processingText=false}, when processing is false, processingText is "" 
            const {processing=true, processingText=""} = action.payload;

            return ({...state, processing: processing, processingText: processing==false ? "" : processingText});
        },

        setPanes: (state, action) => ({...state, panes: {...action.payload}}),

        setPageValue: (state, action) => ({...state, [action.payload.id]: action.payload.value}),
        
        setFormValue: (state, action) => {
            //payload: {id: fieldId, value: fieldValue}
            let id = action.payload.id;
            let value = action.payload.value;

            return ({
                ...state, 
                formValues: {...state.formValues, [id]: value}
            });
        },

        setFormClientErrors: (state, action) => ({...state, formClientErrors: {...action.payload}}),

        setFormServerErrors: (state, action) => ({...state, formServerErrors: [...action.payload]}),

        pageLoaded : (state, action) => {
            //payload: {Groups: , Users: , Clients:  }
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;

            return {...state, 
                pageLoaded: true,
                processing: false, processingText: "",
                mode: 0, 
                formKey: (new Date()).toString(), formValues:{...defaultFormInitialValues}, formInitialValues:{...defaultFormInitialValues}, formClientErrors:{}, formServerErrors:[],            
                lookups:{ 
                    groups: action.payload.Groups,
                    types: defaultTypes
                },
                entities: action.payload.Users
            };
        },
      
        entityLoaded: (state, action) => {
            //action.payload: the fetched entity + ClientId is "" if null in Db + Username=Password=PasswordConfirmed=""
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;

            const groupsIds = action.payload.GroupsIds;

            return {...state, 
                processing: false, processingText: "",
                mode: 2, 
                formKey: (new Date()).toString(), 
                formInitialValues: {...action.payload, Groups: state.lookups.groups.map(e => ({Id: e.Id, Name: e.Name, Checked: groupsIds.find(g => g == e.Id) ? true : false}))}, 
                formValues: {...action.payload, Groups: state.lookups.groups.map(e => ({Id: e.Id, Name: e.Name, Checked: groupsIds.find(g => g == e.Id) ? true : false}))}, 
                formClientErrors: {}, formServerErrors: [],             
            };
        },         

        createEntity: (state, action) => {
            const pageState = current(state);

            return ({
                ...state, 
                mode: 1, 
                formKey: (new Date()).toString(), 
                formInitialValues: {...defaultFormInitialValues, Groups: state.lookups.groups.map(e => ({Id: e.Id, Name: e.Name, Checked: false}))}, 
                formValues: {...defaultFormInitialValues, Groups: state.lookups.groups.map(e => ({Id: e.Id, Name: e.Name, Checked: false}))}, 
                formClientErrors: {}, formServerErrors: []
            });
        },

        entityInserted: (state, action) => {
            //payload: id
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;

            const id = action.payload;

            const insertedEntity = {...pageState.formValues, Id: id};
             
            return ({
                ...state, 
                mode: 2, 
                formKey: (new Date()).toString(), 
                formValues: {...insertedEntity}, formInitialValues: {...insertedEntity}, formClientErrors: {}, formServerErrors: [],
                entities: [...state.entities, insertedEntity]
            });
        },

        entityUpdated: (state, action) => {
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;

            const entity = pageState.formValues;      
            
            //reflect on entities.name

            return ({
                ...state, 
                formKey: (new Date()).toString(), formInitialValues: {...state.formValues}, formClientErrors:{}, formServerErrors: [],
                entities: state.entities.map(e => {
                    if (e.Id == entity.Id)
                        return {...e, Name: entity.Name};
                    
                    return e;
                })
            });
        },

        entityActivatedDeactivated: (state, action) => {
            const pageState = current(state);
         
            const entityId = action.payload.id;
            const activate = action.payload.activate;

            if (pageState.pageDisposed)
                return state;

            const entity = pageState.formValues;      
            
            return ({
                ...state, 
                formKey: (new Date()).toString(), 
                formInitialValues: {...state.formInitialValues, IsActive: activate}, 
                formValues: {...state.formInitialValues, IsActive: activate}, 
                formClientErrors:{}, formServerErrors: [],
                entities: state.entities.map(e => {
                    if (e.Id == entityId)
                        return {...e, IsActive: activate};
                    
                    return e;
                })
            });
        },
        
        removeEntityFromUI: (state, action) => {
            //payload: the entity id
            let pageState = current(state);
            let entityId = action.payload;

            //remove from entities 
            let entities = pageState.entities.filter(e => e.Id != entityId);

            //if it's the current entity => goto no mode
            let currentMode = {};
            if (pageState.mode == 2 && pageState.formValues.Id == entityId)
               currentMode = {mode: 0, formKey: (new Date()).toString(), formInitialValues: {...defaultFormInitialValues}, formValues: {...defaultFormInitialValues}, formClientErrors: {}, formServerErrors: [] };

            return ({...state, entities: entities, ...currentMode});
        },

        entityDeleted: (state, action) => {
            //payload: id of the deleted entity
            const pageState = current(state);
            if (pageState.pageDisposed)
                return state;

            const deletedEntityId = action.payload;
            let isCurrentEntity = (pageState.formValues.Id == deletedEntityId);
            
            let newMode={};

            if(isCurrentEntity)
                newMode={
                    mode: 0, 
                    formKey: (new Date()).toString(), formInitialValues: {...defaultFormInitialValues}, formValues: {...defaultFormInitialValues}, 
                    formClientErrors: {}, formServerErrors: []
                };

            return ({...state, entities: state.entities.filter(e => e.Id != deletedEntityId), ...newMode});
        },       

        disposePage: (state, action) => {
            //payload: bool  
            if (action.payload)
                return {...initialState};
            else  
                return {...state, pageDisposed: false};
        },
    }
});

export default usersPageSlice.reducer;


export const loadPage = (t) => async (dispatch, getState) => {
    //start with initial values
    dispatch(setInitialState());

    //undispose page
    dispatch(disposePage(false));

    //show loader
    dispatch(setProcessing({processing: true, processingText: t("slices.loadingPage")}));

    try{
        var response = await api({            
                url: 'auth/getuserspagedata',
                method:  'post',
                data: {
                    groupId: 0
                },
                //headers:{'h':'1'}
        });
        
        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().usersPage.pageDisposed)
            return;
           
        //it's ok to dispatch here
        let data = response.data.Data;       
       
        dispatch(pageLoaded(data));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false        
        return Promise.reject(err);
    }
};

export const loadEntity = (entityId, t) => async (dispatch, getState) => {
    //show loader
    dispatch(setProcessing({processing: true, processingText: t("slices.loadingUser")}));

    try{
        var response = await api({            
            url: "auth/getuserbyid",
            method:  'post',
            data: {
                id: entityId
            },
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().usersPage.pageDisposed)
           return;

        let data = response.data.Data;

        //transform data
        if(data.ClientId == null)
           data.ClientId = "";

        data.Username = "";
        data.Password = "";
        data.PasswordConfirmed = "";

        dispatch(entityLoaded(data));
        
        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const saveEntity = (t) => async (dispatch, getState) => {
    const pageState = getState().usersPage;

    let entity = pageState.formValues;
    let entityId = entity.Id;
    let url = pageState.mode == 1 ? "auth/createuser" : "auth/updateuser";

    //show loader
    dispatch(setProcessing({processing: true, processingText: t("slices.savingUser")}));

    try{
        var response = await api({            
            url:url,
            method:  'post',
            data: {
                ...pageState.formValues,
                ClientId: pageState.formValues.ClientId == "" ? null : parseInt(entity.ClientId)
            },
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().usersPage.pageDisposed)
           return;

        let data = response.data.Data;

        if (pageState.mode == 1)
            dispatch(entityInserted(data.Id));
        else
            dispatch(entityUpdated());
        
        let successMessage = pageState.mode == 1 ? t("slices.savedUser") : t("slices.updatedUser");

        dispatch(addNotification({text: successMessage, type: "success", closable: true, duration:notificationDurationMiddle, position:"topRight"}));

        dispatch(setProcessing({processing: false}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const activateDeactivateEntity = (activate, t) => async (dispatch, getState) => {
    const pageState = getState().usersPage;

    let entity = pageState.formValues;
    let entityId = entity.Id;
    let url = "auth/activatedeactivateuser";

    //show loader
    dispatch(setProcessing({processing: true, processingText: activate ?  t("slices.activatingUser") : t("slices.deactivatingUser")}));

    try{
        var response = await api({            
            url:url,
            method:  'post',
            data: {
                Id: entityId,
                Activate: activate
            },
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().usersPage.pageDisposed)
           return;

        let data = response.data.Data;      
        
        let successMessage = activate ? t("slices.activatedUser") : t("slices.deactivatedUser");

        dispatch(entityActivatedDeactivated({id: entityId, activate: activate}));

        dispatch(addNotification({text: successMessage, type: "success", closable: true, duration:notificationDurationMiddle, position:"topRight"}));

        dispatch(setProcessing({processing: false}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const deleteEntity = (id, t) => async (dispatch, getState) => {
    //payload the entity id
    dispatch(setProcessing({processing: true, processingText: t("slices.deletingUser")}));

    try{
        var response = await api({            
            url:"/auth/removeuser",
            method:  'post',
            data: {id: id},
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().usersPage.pageDisposed)
           return;

        let data = response.data.Data;

        dispatch(addNotification({text: t("slices.deletedUser"), type: "success", closable: true, duration:3, position:"topRight"}));

        dispatch(entityDeleted(id));

        if (data.ToRefresh === true || data.ToRefresh === "true") {
            window.location.reload();
        }

        dispatch(setProcessing({processing: false}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};


export const { setInitialState, setProcessing, setPageValue, pageLoaded, setPanes, createEntity, setFormValue, setFormClientErrors, setFormServerErrors, entityLoaded, removeEntityFromUI, entityInserted, entityUpdated, entityActivatedDeactivated,entityDeleted, disposePage } = usersPageSlice.actions;

